import React, { useEffect, useState } from "react";
import {
  GraduationCap, Lock, Unlock, Save, ChevronDown, CheckCircle2, Circle, UserCircle2,
  ListChecks, AlertTriangle,
} from "lucide-react";
import { apiGet, apiPatch, apiPost, unpackList } from "../../Api/Client.js";
import { loadProgressView, saveOptimistically } from "./onboardingProgress.js";
import { resolveActiveEmployee } from "../../Shared/ScreenUtils.jsx";
import InfoTooltip from "../../Shared/InfoTooltip.jsx";
import { SlackIntegrationHealthBanner } from "../../Shared/ScreenComponents.jsx";

// Sales Onboarding (senior/AE track) — for full-timers in Sales.
// Per-person, real, backed by RevOps.OnboardingProgress — same model and same
// access pattern as Junior Onboarding: a person picker for whoever holds
// UPDATE on this page (Bassi, today), read-only self-view for everyone else.
// Stage content (Docs/RevOps ladder for "senior") is sourced from
// Docs/TRACK_2_SENIOR_AE.md, not the 8 scorecard parameters restated.

const card = { background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 14, padding: "18px 20px" };
const muted = { color: "var(--c-500)" };
const chip = (bg, fg) => ({
  display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700,
  letterSpacing: "0.04em", textTransform: "uppercase", padding: "3px 9px", borderRadius: 999,
  background: bg, color: fg,
});

function Task({ done, onToggle, readOnly, children }) {
  const Icon = done ? CheckCircle2 : Circle;
  return (
    <button
      onClick={readOnly ? undefined : onToggle}
      style={{ display: "flex", alignItems: "flex-start", gap: 10, width: "100%", textAlign: "left", background: "transparent", border: "none", cursor: readOnly ? "default" : "pointer", padding: "7px 0", color: done ? "var(--c-500)" : "var(--c-900)" }}
    >
      <Icon size={17} color={done ? "var(--green-600)" : "var(--c-500)"} style={{ marginTop: 1, flexShrink: 0 }} />
      <span style={{ fontSize: 13.5, lineHeight: 1.5, textDecoration: done ? "line-through" : "none" }}>{children}</span>
    </button>
  );
}

function StageSection({ stage, tasksDone, onToggleTask, unlocked, onToggleLock, canEdit, isCurrent }) {
  const allDone = stage.tasks.length > 0 && stage.tasks.every((_, i) => tasksDone?.[i]);
  const locked = !unlocked && !isCurrent && !allDone;
  return (
    <div style={{ ...card, opacity: locked && !canEdit ? 0.6 : 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        {canEdit ? (
          <button onClick={onToggleLock} title={unlocked ? "Click to lock again" : "Click to unlock"} style={{ background: "transparent", border: "none", cursor: "pointer", padding: 2 }}>
            {unlocked ? <Unlock size={17} color="var(--green-600)" /> : <Lock size={17} color="var(--c-400)" />}
          </button>
        ) : (
          unlocked ? <Unlock size={17} color="var(--green-600)" /> : <Lock size={17} color="var(--c-400)" />
        )}
        <h2 style={{ margin: 0, fontSize: 16, color: allDone ? "var(--c-500)" : "var(--c-900)", textDecoration: allDone ? "line-through" : "none" }}>{stage.label}</h2>
        {allDone && <CheckCircle2 size={15} color="var(--green-600)" />}
        {isCurrent && <span style={chip("var(--blue-50, rgba(80,120,255,.12))", "var(--blue-600)")}>current stage</span>}
      </div>
      <p style={{ margin: "0 0 10px", fontSize: 13, ...muted }}>{stage.text}</p>
      {(unlocked || isCurrent || canEdit) ? (
        stage.tasks.map((t, i) => (
          <Task key={i} done={!!tasksDone?.[i]} onToggle={() => onToggleTask(i)} readOnly={!canEdit}>{t}</Task>
        ))
      ) : (
        <div style={{ fontSize: 12.5, ...muted }}>Locked.</div>
      )}
    </div>
  );
}

function PersonPicker({ people, selectedId, onSelect }) {
  return (
    <div style={{ position: "relative" }}>
      <select
        value={selectedId || ""}
        onChange={(e) => onSelect(Number(e.target.value))}
        style={{ appearance: "none", padding: "8px 32px 8px 14px", borderRadius: 999, border: "1px solid var(--c-200)", background: "var(--blue-50, rgba(80,120,255,.1))", color: "var(--blue-600)", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
      >
        {people.map((p) => <option key={p.employee_id} value={p.employee_id}>{p.name}</option>)}
      </select>
      <ChevronDown size={14} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} color="var(--blue-600)" />
    </div>
  );
}

export default function SeniorOnboardingScreen({ data }) {
  const me = data?.me;
  // Superuser-only, same reasoning as the junior screen — see its comment. A
  // delegated non-superuser manager is a real future need, not this check yet.
  const canEdit = !!me?.user?.isSuperuser;
  const myEmployee = resolveActiveEmployee(data);

  const [ladderConfig, setLadderConfig] = useState([]); // rubric.senior.ladder.stages; an empty list until (or unless) the rubric loads, never null
  const [shortlistCheckpoints, setShortlistCheckpoints] = useState([]);
  const [commonCorrections, setCommonCorrections] = useState([]);
  const [parameters, setParameters] = useState([]);
  const [stages, setStages] = useState([]); // Stage enum options for the dropdown
  const [people, setPeople] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [progressFailed, setProgressFailed] = useState(false); // the load failed: not the same as "no record yet"
  const [error, setError] = useState("");
  const [notes, setNotes] = useState("");
  const [blockers, setBlockers] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);
  const [notesBusy, setNotesBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiGet("/RevOps/rubric/").then((r) => {
      if (cancelled) return;
      setLadderConfig(r?.senior?.ladder?.stages || []);
      setShortlistCheckpoints(r?.senior?.ladder?.shortlist_checkpoints || []);
      setCommonCorrections(r?.senior?.ladder?.common_early_corrections || []);
      setParameters(r?.senior?.parameters || []);
      setStages(r?.senior?.stages || []);
    }).catch(() => !cancelled && setError("The onboarding ladder could not be loaded. Reload the page to try again."));
    if (canEdit) {
      apiGet("/RevOps/eligible-people/?track=senior")
        .then((r) => { if (!cancelled) { setPeople(r.people || []); setSelectedId((r.people || [])[0]?.employee_id || null); } })
        .catch((err) => !cancelled && setError(err?.message || "Failed to load the roster."));
    } else if (myEmployee?.id) {
      setSelectedId(myEmployee.id);
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit]);

  useEffect(() => {
    if (!selectedId) return undefined;
    let cancelled = false;
    loadProgressView({
      fetchRow: async () => unpackList(await apiGet(`/RevOps/SeniorOnboardingProgress/?employee=${selectedId}`))[0] || null,
      isCurrent: () => !cancelled,
      show: (v) => { setProgress(v.progress); setNotes(v.notes); setBlockers(v.blockers); setNotesDirty(v.notesDirty); },
      onStart: () => { setProgressLoading(true); setProgressFailed(false); setError(""); },
      onFail: (err) => { setProgressFailed(true); setError(err?.message || "Failed to load onboarding progress."); },
      onDone: () => setProgressLoading(false),
    });
    return () => { cancelled = true; };
  }, [selectedId]);

  const ladder = progress?.ladder_state && Object.keys(progress.ladder_state).length
    ? progress.ladder_state
    : { completed: {}, unlocked: [] };

  const startOnboarding = async () => {
    try {
      const created = await apiPost("/RevOps/SeniorOnboardingProgress/", {
        employee: selectedId, stage: "senior_reset", ladder_state: { completed: {}, unlocked: [] },
      });
      setProgress(created);
    } catch (err) {
      alert(err?.message || "Failed to start onboarding for this person.");
    }
  };

  const patchLadder = async (next) => {
    if (!progress) return;
    await saveOptimistically({
      progress,
      patch: { ladder_state: next },
      setProgress,
      save: () => apiPatch(`/RevOps/SeniorOnboardingProgress/${progress.id}/`, { ladder_state: next, inferred: false }),
      onFail: (err) => alert(err?.message || "Failed to save."),
    });
  };

  const toggleTask = (stageKey, i) => {
    const list = [...(ladder.completed?.[stageKey] || [])];
    list[i] = !list[i];
    patchLadder({ ...ladder, completed: { ...ladder.completed, [stageKey]: list } });
  };
  const toggleLock = (stageKey) => {
    const unlocked = new Set(ladder.unlocked || []);
    unlocked.has(stageKey) ? unlocked.delete(stageKey) : unlocked.add(stageKey);
    patchLadder({ ...ladder, unlocked: Array.from(unlocked) });
  };

  const saveStage = async (stage) => {
    await saveOptimistically({
      progress,
      patch: { stage },
      setProgress,
      save: () => apiPatch(`/RevOps/SeniorOnboardingProgress/${progress.id}/`, { stage, inferred: false }),
      onFail: (err) => alert(err?.message || "Failed to save."),
    });
  };

  const saveNotes = async () => {
    setNotesBusy(true);
    try {
      const updated = await apiPatch(`/RevOps/SeniorOnboardingProgress/${progress.id}/`, { notes, blockers, inferred: false });
      setProgress(updated);
      setNotesDirty(false);
    } catch (err) {
      alert(err?.message || "Failed to save.");
    } finally {
      setNotesBusy(false);
    }
  };

  const selectedPerson = people?.find((p) => p.employee_id === selectedId);
  const displayName = canEdit ? (selectedPerson?.name || "…") : (myEmployee?.display_name || "Your");

  return (
    <section className="Screen-Stack" style={{ padding: "26px 30px", maxWidth: 980, margin: "0 auto" }}>
      <SlackIntegrationHealthBanner />
      <section className="Page-Heading" style={{ marginBottom: 6, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <span>Onboarding</span>
          <h1>{displayName}{canEdit ? "'s" : ""} Onboarding</h1>
        </div>
        {canEdit && people && people.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <PersonPicker people={people} selectedId={selectedId} onSelect={setSelectedId} />
            <InfoTooltip>
              <strong style={{ display: "block", marginBottom: 6, color: "var(--c-900)" }}>How this list is built</strong>
              Whoever holds <b>read</b> access to the Sales Onboarding page shows up here — nothing more.
              There's no separate roster; the access grant IS the definition of who's on this track.
              <br /><br />
              That's normally every full-timer in Sales — they get it by default from their
              department the moment they convert from intern. Change someone's department away from
              Sales and they lose it automatically; if you want them here anyway, grant it to
              them by hand from the admin panel.
              <br /><br />
              <strong style={{ color: "var(--c-900)" }}>To onboard someone new:</strong> Admin Panel → People →
              click their access → tick <b>Sales Onboarding: read</b>. The moment that's granted, their
              name appears in this list, with an empty record ready to fill in. A junior who's progressed
              fast can be given this even while still technically an intern — it's a manual grant, not
              locked to employment type.
              <br /><br />
              Only people who hold <b>update</b> on this page see the dropdown at all — today that's you.
            </InfoTooltip>
          </div>
        )}
      </section>
      <p style={{ margin: "0 0 20px", ...muted, fontSize: 14 }}>
        The senior track — live coaching on real deals, then the weekly standup cadence.
        {!canEdit && " This is a reference page — your progress is judged and updated by your manager."}
      </p>

      {error && <div className="Login-Error">{error}</div>}

      {canEdit && people && people.length === 0 && (
        <div style={{ ...card, borderStyle: "dashed" }}>
          <p style={{ margin: 0, fontSize: 13, ...muted }}>
            Nobody currently holds Sales Onboarding access — grant it to a full-timer from the admin panel's People list first.
          </p>
        </div>
      )}

      {selectedId && progressLoading && <p style={muted}>Loading…</p>}

      {selectedId && !progressLoading && !progressFailed && !progress && (
        <div style={{ ...card, borderStyle: "dashed", textAlign: "center" }}>
          <UserCircle2 size={22} color="var(--c-400)" />
          <p style={{ margin: "8px 0 0", fontSize: 13, ...muted }}>
            {canEdit
              ? "No onboarding record for this person yet."
              : "No onboarding record for you yet. Please ask your mentor to start your onboarding."}
          </p>
          {canEdit && (
            <button className="Primary-Button" onClick={startOnboarding} style={{ marginTop: 10 }}>
              Start onboarding
            </button>
          )}
        </div>
      )}

      {selectedId && !progressLoading && progress && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          <div style={{ ...card, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div>
              <strong style={{ fontSize: 14, color: "var(--c-900)" }}>Current stage</strong>
              {progress.inferred && <div style={{ fontSize: 11, ...muted, marginTop: 2 }}>Inferred from the session record — not yet confirmed.</div>}
            </div>
            {canEdit ? (
              <select
                value={progress.stage}
                onChange={(e) => saveStage(e.target.value)}
                style={{ padding: "6px 12px", borderRadius: 999, border: "1px solid var(--c-200)", background: "var(--blue-50, rgba(80,120,255,.1))", color: "var(--blue-600)", fontSize: 12, fontWeight: 700 }}
              >
                {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            ) : (
              <span style={chip("var(--blue-50, rgba(80,120,255,.1))", "var(--blue-600)")}>{progress.stage_label}</span>
            )}
          </div>

          {ladderConfig.map((stage) => (
            <StageSection
              key={stage.key}
              stage={stage}
              tasksDone={ladder.completed?.[stage.key] || []}
              onToggleTask={(i) => toggleTask(stage.key, i)}
              unlocked={(ladder.unlocked || []).includes(stage.key)}
              onToggleLock={() => toggleLock(stage.key)}
              canEdit={canEdit}
              isCurrent={progress.stage === stage.key}
            />
          ))}

          <div style={card}>
            <strong style={{ fontSize: 14, color: "var(--c-900)", display: "block", marginBottom: 8 }}>What we've seen — session by session</strong>
            <textarea
              rows={10}
              value={notes}
              readOnly={!canEdit}
              onChange={(e) => { setNotes(e.target.value); setNotesDirty(true); }}
              style={{ width: "100%", resize: "vertical", fontSize: 13, lineHeight: 1.7, padding: 8, borderRadius: 8, border: "1px solid var(--c-200)", marginBottom: 10, fontFamily: "inherit", background: canEdit ? undefined : "var(--surface-soft, #f5f5f5)" }}
            />
            <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: "var(--c-600)", marginBottom: 3 }}>What's holding them at this rung</label>
            <textarea
              rows={4}
              value={blockers}
              readOnly={!canEdit}
              onChange={(e) => { setBlockers(e.target.value); setNotesDirty(true); }}
              style={{ width: "100%", resize: "vertical", fontSize: 13, lineHeight: 1.7, padding: 8, borderRadius: 8, border: "1px solid var(--c-200)", marginBottom: canEdit ? 10 : 0, fontFamily: "inherit", background: canEdit ? undefined : "var(--surface-soft, #f5f5f5)" }}
            />
            {canEdit && (
              <button className="Primary-Button" onClick={saveNotes} disabled={notesBusy || !notesDirty} style={{ opacity: notesDirty ? 1 : 0.5 }}>
                {notesBusy ? "Saving…" : "Save"} <Save size={14} />
              </button>
            )}
          </div>

          {/* Reference material — behind onboarding, not a general-purpose card.
              Only visible once a record exists. Compressed pass/fail gates and
              the recurring basics, same treatment the junior track gives its
              w1/w2 task lists — a lot of skill folded into a short bullet. */}
          {shortlistCheckpoints.length > 0 && (
            <div style={{ ...card, background: "transparent" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <ListChecks size={18} color="var(--c-500)" />
                <strong style={{ color: "var(--c-600)", fontSize: 14 }}>What actually shortlists someone — the pass/fail gates</strong>
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 }}>
                {shortlistCheckpoints.map((line, i) => (
                  <li key={i} style={{ fontSize: 12.5, lineHeight: 1.6, ...muted }}>{line}</li>
                ))}
              </ul>
            </div>
          )}
          {commonCorrections.length > 0 && (
            <div style={{ ...card, background: "transparent" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <AlertTriangle size={18} color="var(--c-500)" />
                <strong style={{ color: "var(--c-600)", fontSize: 14 }}>Basics that trip up almost everyone early</strong>
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 }}>
                {commonCorrections.map((line, i) => (
                  <li key={i} style={{ fontSize: 12.5, lineHeight: 1.6, ...muted }}>{line}</li>
                ))}
              </ul>
            </div>
          )}
          {parameters.length > 0 && (
            <div style={{ ...card, background: "transparent" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <GraduationCap size={18} color="var(--c-500)" />
                <strong style={{ color: "var(--c-600)", fontSize: 14 }}>What a senior is judged on</strong>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {parameters.map((p) => (
                  <div key={p.key}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--c-900)" }}>{p.label}</div>
                    {p.zero && p.ten && (
                      <div style={{ fontSize: 12.5, lineHeight: 1.6, ...muted }}>
                        <span style={{ color: "var(--red-600, #c0392b)" }}>0 —</span> {p.zero}{" "}
                        <span style={{ color: "var(--green-600)" }}>10 —</span> {p.ten}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11.5, marginTop: 10, ...muted }}>
                Same eight parameters the scorecard reads calls against — see RevOps → Seller Scorecard.
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 8, ...muted, fontSize: 12, marginTop: 12 }}>
        <Lock size={13} /> Visible to full-timers in Sales.
      </div>
    </section>
  );
}
